// src/pages/admin/DepartmentDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebase-config';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import DepartmentSettings from '../../components/departments/DepartmentSettings';
import DepartmentStaffList from '../../components/departments/DepartmentStaffList';
import { useDepartment } from '../../contexts/DepartmentContext';

function DepartmentDetailPage() {
  const { t } = useTranslation('departments');
  const { t: tCommon } = useTranslation('common');
  const { departmentId } = useParams();
  const navigate = useNavigate();
  const { currentUser, currentCompany } = useAuth(); // ARCHIVE: added currentCompany
  const { deleteDepartment } = useDepartment();

  const [department, setDepartment] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('staff');

  useEffect(() => {
    const fetchDepartmentAndStaff = async () => {
      if (!currentUser || !departmentId) return;

      setLoading(true);
      try {
        // ARCHIVE: was `currentUser.uid` unconditionally — same bug as the
        // department staff forms, fixed here for consistency.
        const companyId = currentCompany?.id || currentUser.uid;

        const departmentRef = doc(db, 'companies', companyId, 'departments', departmentId);
        const departmentSnap = await getDoc(departmentRef);

        if (!departmentSnap.exists()) {
          setError(t('noDepatments'));
          setLoading(false);
          return;
        }

        const departmentData = {
          id: departmentSnap.id,
          ...departmentSnap.data()
        };

        setDepartment(departmentData);

        const staffRef = collection(db, 'companies', companyId, 'staff');
        const q = query(staffRef, where('departmentIds', 'array-contains', departmentId));
        const staffSnap = await getDocs(q);

        const staffList = staffSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // ARCHIVE (§6): hide archived staff from the department detail view.
        // Never `where('active','==',true)` — filter in memory so legacy
        // docs with no `active` field still count as active.
        setStaff(staffList.filter(s => s.active !== false));
        setError('');
      } catch (err) {
        console.error('Error fetching department details:', err);
        setError(tCommon('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchDepartmentAndStaff();
  }, [currentUser, currentCompany, departmentId, t, tCommon]);

  const handleDeleteDepartment = async () => {
    if (window.confirm(t('confirmDelete') + ' ' + t('deleteWarning'))) {
      try {
        const success = await deleteDepartment(departmentId);
        if (success) {
          navigate('/admin/departments');
        }
      } catch (err) {
        console.error('Error deleting department:', err);
        setError(tCommon('common.error'));
      }
    }
  };

  const handleRefreshStaff = async () => {
    try {
      // ARCHIVE: same companyId fix
      const companyId = currentCompany?.id || currentUser.uid;
      const staffRef = collection(db, 'companies', companyId, 'staff');
      const q = query(staffRef, where('departmentIds', 'array-contains', departmentId));
      const staffSnap = await getDocs(q);

      const staffList = staffSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // ARCHIVE (§6): same filter on refresh, so an archive/reactivate
      // action taken from within this page's staff list doesn't leave a
      // stale archived row visible until a full page reload.
      setStaff(staffList.filter(s => s.active !== false));
    } catch (err) {
      console.error('Error refreshing staff list:', err);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return <div className="field-error">{error}</div>;
  }

  if (!department) {
    return <div className="not-found">{t('noDepatments')}</div>;
  }

  return (
    <div className="department-detail-page">

      <div className="department-header">
        <h1>{department.name}</h1>
      </div>

      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'staff' ? 'active' : ''}`}
          onClick={() => setActiveTab('staff')}
        >
          {t('staffMembers')}
        </button>
        <button
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          {t('settings')}
        </button>
      </div>

      <div className="tab-content">

        {activeTab === 'staff' && (
          <DepartmentStaffList
            departmentId={departmentId}
            staff={staff}
            onStaffListChanged={handleRefreshStaff}
          />
        )}

        {activeTab === 'settings' && (
          <>
            <DepartmentSettings
              department={department}
              onDepartmentUpdated={(updatedDepartment) => {
                setDepartment(updatedDepartment);
              }}
            />

            <div className="delete-section">
              <div className="delete-section-header">
                <h2 className="delete-section-title">{t('deleteDepartment')}</h2>
              </div>
              <div className="delete-section-body">
                <p className="delete-section-description">{t('deleteWarning')}</p>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleDeleteDepartment}
                >
                  {t('deleteDepartment')}
                </button>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

export default DepartmentDetailPage;